<template>
  <layout>
    <template v-slot:header>
      <AppHead title="Products" />
      <div class="mb-2">
        <h2 class="m-0">Products</h2>
        <p v-if="filterSelected" class="m-0 text-primary cursor-pointer" @click="clearFilters">
          <i class="fas fa-times mr-2"></i> Clear Filters
        </p>
      </div>

      <BreadCrumbs activeItem="Products" :crumbs="{ home: 'Dashboard' }"/>
      <flash-messages />
    </template>

    <template v-slot:default>
      <card class="py-4">
        <div class="d-flex flex-column flex-xl-row">
          <jet-button class="my-1 my-xl-0" :goesTo="route('products.create')" icon="fas fa-plus">Create Product</jet-button>
          <jet-button class="ml-0 ml-xl-3 my-1 my-xl-0" :goesTo="route('categories')" icon="fas fa-list">Manage Categories</jet-button>
          <jet-button class="ml-0 ml-xl-3 my-1 my-xl-0" :goesTo="route('manufacturers')">Manage Manufacturers</jet-button>
          <jet-button class="ml-0 ml-xl-3 my-1 my-xl-0" :goesTo="route('admin.sales')">Manage Sales</jet-button>
          <jet-button class="ml-0 ml-xl-3 my-1 my-xl-0" :goesTo="route('inventory')" icon="fas fa-truck">Check in Inventory</jet-button>
          <Search class="ml-0 ml-xl-3 my-1 my-xl-0" :searchText="searchText" @searched="search"/>
        </div>

        <hr class="my-4">

        <div class="col-12 col-lg-5 px-0 mt-2">
          <card title="Filter Results" headerStyle="dark">
            <div class="mb-3">
              <jet-label for="filter_active" value="Active"/>
              <select class="form-control" v-model="filter_active" @change="activeFilterChanged"> 
                <option :value="-1">All</option>
                <option :value="1">Active</option>
                <option :value="0">Not Active</option>
              </select>
            </div>
            <div class="mb-3">
              <label class="form-label mb-0">Category:</label>
              <Multiselect
                ref="category-select"
                v-model="categories_selected"
                mode="tags"
                :options="filtered_categories"
                :searchable="true"
                class="admin-multiselect input-short"
                id="category_select"
                @change="category"
              />
            </div>

            <div>
              <label class="form-label mb-0">Manufacturer:</label>
              <Multiselect
                v-model="manufacturers_selected"
                mode="tags"
                :options="manufacturers"
                :searchable="true"
                class="admin-multiselect"
                id="manufacturer_select"
                @change="manufacturer"
              />
            </div>
          </card>
        </div>
      </card>

      <DataTable 
        ref="admin-products-index"
        :value="products" 
        :paginator="true"
        :lazy="true"
        :first="(pagination.page - 1) * pagination.per_page"
        :rows="pagination.per_page"
        :totalRecords="pagination.total"
        @page="onPage($event)"
        @state-restore="onTableStateRestore($event)"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate="({first} - {last} of {totalRecords})" 
        paginatorPosition="both" removableSort
        :rowsPerPageOptions="[10, 20, 50, 100]"
        :sortField="sortField"
        :sortOrder="sortOrder"
        class="p-datatable-sm admin-datatable"
        :class="{'hide-select-all': products.length > 50}"
        tableClass="border-left border-right border-bottom"
        stateStorage="local" 
        stateKey="admin-products-index"
        dataKey="id"
        breakpoint="992px" 
        v-model:selection="selectedProducts"
        @row-click="(e) => { rowClick(e) }"
        @sort="onSort"
        v-model:expandedRows="expandedRows"
      >
        <Column expander style="width: 2rem" />
        <template #expansion="slotProps">
          <div class="p-3 px-5">
              <h5>Inventory Locations</h5>
              <InventoryLocation :inventoryLocations="slotProps.data.inventory_location ? [slotProps.data.inventory_location] : []"></InventoryLocation>
          </div>
        </template>
        <Column class="button-container selection-mode-box" selectionMode="multiple" headerStyle="width: 3em"></Column>
        <Column field="name" header="Name" :sortable="true">
          <template #body="slotProps">
                    <i v-if="slotProps.data.image_url" @click="e => toggleOverlay(e, slotProps.data.id)" title="View product image" class="fas fa-image image-icon text-dark text-size-small cursor-pointer h-100 fa-2x"></i>
                    <OverlayPanel v-if="slotProps.data.image_url" :ref="`product-list-image-overlay${slotProps.data.id}`" :showCloseIcon="true" :dismissable="true">
                      <img :src="slotProps.data.image_url" />
                    </OverlayPanel>
                  
            {{ slotProps.data.name }}
            <span v-if="!slotProps.data.is_active" class="badge badge-danger" @click="toggleActive(slotProps.data.id)">Inactive</span>
            <span v-if="slotProps.data.product_type == 'variation_included'" class="badge badge-success">Variation Included</span>
            <span v-else-if="slotProps.data.product_type == 'variation_main'" class="badge badge-success">Variation Main</span>
            <span v-else-if="slotProps.data.product_type == 'bundle_single_with_variation_included'" class="badge badge-success">Bundle of Variation Main</span>
            <span v-else-if="slotProps.data.product_type == 'bundle_single'" class="badge badge-success">Single Bundle</span>
            <span v-else-if="slotProps.data.product_type == 'bundle_multi'" class="badge badge-success">Multi Bundle</span>
          </template>
        </Column>
        <Column field="sku" header="SKU" :sortable="true"></Column>
        <Column field="price_msrp" header="MSRP" :sortable="true">
          <template #body="slotProps">
            {{ formatDollars(slotProps.data.price_msrp) }}
          </template>
        </Column>
        <Column field="price_mtn" header="MTN Price" :sortable="true">
          <template #body="slotProps">
            {{ formatDollars(slotProps.data.price_mtn) }}
          </template>
        </Column>
        <Column field="price_wholesale" header="MTD Price" :sortable="true">
          <template #body="slotProps">
            {{ formatDollars(slotProps.data.price_wholesale) }}
          </template>
        </Column>
        <Column field="inventoryInfo" header="Inventory" :sortable="true" sortField="inventoryInfo.current_inventory">
          <template #body="slotProps">
            <p class="m-0">
              <InventoryInfo :inventoryInfo="slotProps.data.inventoryInfo" />
            </p>
          </template>
        </Column>
        <Column field="created_at" header="Created" :sortable="true">
          <template #body="slotProps">
            {{ moment(slotProps.data.created_at).format('M/D/YY h:mm A') }}
          </template>
        </Column>
        <Column field="updated_at" header="Updated" :sortable="true">
          <template #body="slotProps">
            {{ moment(slotProps.data.updated_at).format('M/D/YY h:mm A') }}
          </template>
        </Column>
        <Column header="Action" class="button-container hide-title-mobile" :exportable="false">
          <template #body="slotProps">
            <inertia-link class="px-2 px-lg-4" :href="route('products.togglenew', slotProps.data.id)" tabindex="-1" preserve-scroll>
              <span v-if="slotProps.data.is_new" class="font-weight-bold text-nowrap">New -</span>
              <span v-else class="text-muted text-nowrap">New +</span>
            </inertia-link>
            <inertia-link class="px-2 px-lg-4" :href="route('products.togglehot', slotProps.data.id)" tabindex="-1" preserve-scroll>
              <span v-if="slotProps.data.is_hot" class="font-weight-bold text-nowrap">Hot -</span>
              <span v-else class="text-muted text-nowrap">Hot +</span>
            </inertia-link>
            <jet-button class="btn-short text-nowrap mx-2" :goesTo="route('admin.products.view', slotProps.data.id)" icon="fas fa-chevron-right">View</jet-button>
            <jet-button class="btn-short text-nowrap mx-2" :goesTo="route('products.edit', slotProps.data.id)" icon="fas fa-chevron-right">Edit</jet-button>
          </template>
        </Column>
      </DataTable>

      <div class="col-12 col-lg-5 px-0 mt-2">
        <card title="Add to Category" headerStyle="dark">
          
          <div class="mb-3">
            <label class="form-label mb-0">Category:</label>
              <Multiselect
              v-model="add_category_id" 
              :options="categories"
              :searchable="true"
              class="input-short"
              id="category_add_select"
              @change="addCategory"
            />
          </div>
        </card>
      </div>

    </template>
  </layout>
</template>

<script>
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Admin/Layout'
import mapValues from 'lodash/mapValues'
import SearchFilter from '@/Shared/SearchFilter'
import FlashMessages from '@/Shared/FlashMessages'
import Multiselect from '@vueform/multiselect'
import Search from '@/Shared/Admin/Search'
import BreadCrumbs from '@/Shared/BreadCrumbs'
import JetLabel from '@/Jetstream/Label'
import OverlayPanel from 'primevue/overlaypanel'
import InventoryLocation from './InventoryLocation.vue'
  import InventoryInfo from '@/Components/General/InventoryInfo'

import moment from 'moment'

export default {
  components: {
    Icon,
    SearchFilter,
    Layout,
    Multiselect,
    FlashMessages,
    Search,
    BreadCrumbs,
    InventoryLocation,
    JetLabel,
    OverlayPanel,
    InventoryInfo,
  },
  props: {
    allProducts: Array,
    filters: Object,
    manufacturers: Array,
    categories: Array
  },
  data() {
    return {
      pagination: {
        total: 0,
        per_page: 20,
        page: 1,
      },
      products: this.allProducts,
      expandedRows: [],
      selectedProducts: [],
      searchText: null,
      filtered_categories: this.categories,
      categories_selected: [],
      manufacturers_selected: [],
      filter_active: -1,
      current_sort: null,
      add_category_id: null,
      formProducts: this.$inertia.form({
        product_ids: null
      }),
      formActive: this.$inertia.form({
        _method: 'put',        
        active: 1
      }),
      sortField: null, // current sort field
      sortOrder: null // current sort order
    }
  },
  mounted() {
    this.restoreValues()
  },
  computed: {
    filterSelected() {
      return this.manufacturers_selected.length || this.categories_selected.length || this.searchText
    }
  },
  created() {
    this.moment = moment;
  },
  methods: {
    clearFilters() {
      this.searchText = null
      this.category([])
      this.manufacturer([])
      this.search(null, "")
    },
    activeFilterChanged() {
      this.resetTableState()
      localStorage.setItem('active-filter', this.filter_active)
      this.postSearch()
    },
    restoreValues() {
      if (localStorage.getItem('active-filter')) {
        this.filter_active = JSON.parse(localStorage.getItem('active-filter'))
      }

      if (localStorage.getItem('manufacturer') !== '[]') {
        const storedManufacturer = JSON.parse(localStorage.getItem('manufacturer'))
        if (storedManufacturer) this.manufacturers_selected = storedManufacturer
      }

      if (localStorage.getItem('category') !== '[]') {
        const storedCategory = JSON.parse(localStorage.getItem('category'))
        if (storedCategory) this.categories_selected = storedCategory
      }

      if (localStorage.getItem('search') !== 'undefined') {
        const storedSearch = JSON.parse(localStorage.getItem('search'))
        if (storedSearch) this.searchText = storedSearch
      }

      this.postSearch()
    },
    category(selected) {
      this.resetTableState()
      localStorage.setItem('category', JSON.stringify(selected))

      // control gets this event before model is updated
      this.categories_selected = selected
      this.search(null, this.searchText)
    },
    manufacturer(selected) {
      this.resetTableState()

      localStorage.setItem('manufacturer', JSON.stringify(selected))
      this.manufacturers_selected = selected
      this.search(null, this.searchText)
    },
    search(e, searchText) {
      this.searchText = searchText
      this.resetTableState()
      localStorage.setItem('search', JSON.stringify(searchText))

      this.postSearch()
    },
    onTableStateRestore(e) {
      this.pagination.page = e.first / e.rows + 1
      this.pagination.per_page = e.rows
      this.sortField = e.sortField
      this.sortOrder = e.sortOrder
    },
    onPage(e) {
      this.pagination.page = e.page + 1
      this.pagination.per_page = e.rows
      this.postSearch()
    },
    postSearch() {
      this.current_sort = null;
      axios.post(route('products.search'), {
        query: this.searchText || '',
        categories: this.categories_selected || [],
        manufacturers: this.manufacturers_selected || [],
        filter_active: this.filter_active,
        sort_field: this.sortField,
        sort_order: this.sortOrder,
      }, {
        params: {
          page: this.pagination.page, per_page: this.pagination.per_page
        }
      }).then((response) => {
        if (response.data.success) {
          this.products = response.data.data;
          this.pagination.total = response.data.total
          let filteredCategories = this.categories;

          if (this.manufacturers_selected.length > 0 && filteredCategories.length > 0) {
            filteredCategories = this.categories.filter((category) => response.data.category_ids.includes(category.value))
          }

          this.filtered_categories = filteredCategories
          this.updateActiveCategories()
        }
      })
    },
    resetTableState() {
      this.$refs['admin-products-index'].resetPage()
      this.selectedProducts = []
    },
    updateActiveCategories() {
      const selected = Object.keys(this.categories_selected).map(key => {return this.categories_selected[key]})

      if (!selected.length) return

      for (let i = 0; i < selected.length; i++) {
        const filtered = Object.keys(this.filtered_categories).map(key => { return this.filtered_categories[key].value })

        if (!filtered.includes(selected[i])) this.$refs['category-select'].deselect(selected[i])
      }
    },
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    addCategory(selected) {
      if (!selected) return;
      this.formProducts.product_ids = this.selectedProducts.map((item) => item.id);
      this.formProducts.post(route('admin.products.categoryaddbulk', selected), {
        preserveScroll: true,
        onSuccess: () => {
          this.formProducts.reset();
        }
      });
    },
    rowClick(e) {
      // console.log(e.target); $inertia.get(route('products.edit', e.data.id))
    },
    toggleActive(product) {
      this.formActive.post(this.route('products.update', product), {
        preserveScroll: true,
        onSuccess: () => {
          this.postSearch();
        }
      });
    },
    toggleOverlay(event, id) {
      this.$refs[`product-list-image-overlay${id}`].toggle(event)
    },
    onSort(events) {
      this.sortField = events.sortField;
      this.sortOrder = events.sortOrder;
      this.postSearch();
    }
  },
}
</script>

<style src="@vueform/multiselect/themes/default.css"></style>
